import { Button, message, Typography } from "antd"
import React, { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { useTarif595ReceiptPdfEndUserAppLazyQuery, useTarif595ReceiptsEndUserAppQuery } from "../../generated/graphql"
import { URL_ACCOUNT_ATTENDEES } from "../../models/url"
import css from "./AccountBookingDetails.module.css"

/** Tarif 595: gültiger Beleg einer Buchung als PDF. Sichtbar nur bei tarif595Enabled, siehe AccountBookingDetails. */
const Tarif595BookingReceipts: FC<{ bookingId: string }> = ({ bookingId }) => {
    const { t } = useTranslation()
    const { data, loading, error } = useTarif595ReceiptsEndUserAppQuery({ variables: { bookingId }, fetchPolicy: "network-only" })
    // Das PDF enthält die AHV-Nummer: nicht im Apollo-Cache halten
    const [loadPdf] = useTarif595ReceiptPdfEndUserAppLazyQuery({ fetchPolicy: "no-cache" })
    const [downloading, setDownloading] = useState<string>()

    if (loading || error) return null

    const receipts = data?.tarif595ReceiptsEndUserApp ?? []

    const download = async (receiptId: string, name: string) => {
        setDownloading(receiptId)
        try {
            const pdf = (await loadPdf({ variables: { receiptId } })).data?.tarif595ReceiptPdfEndUserApp
            if (!pdf) throw new Error("PDF fehlt")
            const url = URL.createObjectURL(new Blob([Uint8Array.from(atob(pdf), (char) => char.charCodeAt(0))], { type: "application/pdf" }))
            const link = document.createElement("a")
            link.href = url
            link.download = `${name}.pdf`
            link.click()
            // Sofort freigegeben kann iOS-Safari den Download abbrechen
            setTimeout(() => URL.revokeObjectURL(url), 60000)
        } catch {
            message.error(t("account.tarif595.downloadError", "Der Beleg konnte nicht geladen werden."))
        } finally {
            setDownloading(undefined)
        }
    }

    return (
        <section className={css.section} data-testid='account-booking-details-tarif595'>
            <div className={css.sectionTitle}>{t("account.tarif595.receiptTitle", "Krankenkassenbeleg")}</div>
            {receipts.length === 0 ? (
                <Typography.Text type='secondary' data-testid='account-tarif595-no-receipt'>
                    {t("account.tarif595.noReceipt", "Der Beleg erscheint hier, sobald die Kursgebühr vollständig bezahlt ist und die Schule ihn ausgestellt hat. Dafür braucht sie die AHV-Nummer des Teilnehmers.")}{" "}
                    <Link to={URL_ACCOUNT_ATTENDEES}>{t("account.tarif595.toAttendees", "Zu den Angaben")}</Link>
                </Typography.Text>
            ) : (
                receipts.map(({ id, receiptNumber }) => {
                    const name = `RB-${receiptNumber}`

                    return (
                        <Button key={id} loading={downloading === id} onClick={() => download(id, name)} data-testid='account-tarif595-download'>
                            {t("account.tarif595.download", { name, defaultValue: "Beleg {{name}} als PDF laden" })}
                        </Button>
                    )
                })
            )}
        </section>
    )
}

export default Tarif595BookingReceipts
